import { useMemo, useState } from 'react';
import type { SelectMenuItem } from './__commons__/selectCommons';

export type NavEntry =
    | { type: 'selectAll' }
    | { type: 'item'; item: SelectMenuItem }
    | { type: 'bottom'; item: SelectMenuItem };

export interface UseContextualNavOptions {
    isContextual: boolean;
    filteredItems: SelectMenuItem[];
    bottomMenuItems: SelectMenuItem[];
    hasSelectAll: boolean;
}

export interface UseContextualNavResult {
    navEntries: NavEntry[];
    navIndexMap: Map<SelectMenuItem, number>;
    activeIndex: number;
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
    navCount: number;
    handleNavEntryClick: (
        index: number,
        onSelectAll: () => void,
        onItemClick: (item: SelectMenuItem) => void,
        onBottomClick: (item: SelectMenuItem) => void,
    ) => void;
    activeNavId: (id: string) => string | undefined;
}

export function useContextualNav({
    isContextual,
    filteredItems,
    bottomMenuItems,
    hasSelectAll,
}: UseContextualNavOptions): UseContextualNavResult {
    const [activeIndex, setActiveIndex] = useState(-1);

    const navEntries = useMemo<NavEntry[]>(() => {
        if (!isContextual) {
            return [];
        }
        return [
            ...(hasSelectAll ? [{ type: 'selectAll' as const }] : []),
            ...filteredItems.map((item) => ({ type: 'item' as const, item })),
            ...bottomMenuItems.map((item) => ({ type: 'bottom' as const, item })),
        ];
    }, [isContextual, hasSelectAll, filteredItems, bottomMenuItems]);

    const navCount = navEntries.length;

    const navIndexMap = useMemo(() => {
        const map = new Map<SelectMenuItem, number>();
        navEntries.forEach((entry, i) => {
            if (entry.type === 'item' || entry.type === 'bottom') {
                map.set(entry.item, i);
            }
        });
        return map;
    }, [navEntries]);

    const handleNavEntryClick = (
        index: number,
        onSelectAll: () => void,
        onItemClick: (item: SelectMenuItem) => void,
        onBottomClick: (item: SelectMenuItem) => void,
    ) => {
        const entry = navEntries[index];
        if (!entry) {
            return;
        }
        if (entry.type === 'selectAll') {
            onSelectAll();
        } else if (entry.type === 'item') {
            onItemClick(entry.item);
        } else if (entry.type === 'bottom') {
            onBottomClick(entry.item);
        }
    };

    const activeNavId = (id: string) =>
        activeIndex >= 0 && navEntries[activeIndex] ? `${id}-nav-${activeIndex}` : undefined;

    return {
        navEntries,
        navIndexMap,
        activeIndex,
        setActiveIndex,
        navCount,
        handleNavEntryClick,
        activeNavId,
    };
}
